/**
 * Renders a Mermaid diagram from a ```mermaid fence.
 *
 * Mermaid is large and most replies never contain a diagram, so it is loaded
 * the first time one appears rather than with the assistant. Until it arrives
 * the source is shown as code, which is still readable.
 *
 * Rendering is async and the component can unmount mid-reply, so every result
 * is discarded if the diagram it belongs to is no longer on screen.
 */

import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import CodeBlock from "../CodeBlock";

let mermaidPromise = null;

/** Loaded once per session, shared by every diagram after the first. */
const loadMermaid = () => {
  if (!mermaidPromise) {
    mermaidPromise = import("mermaid").then(({ default: mermaid }) => {
      mermaid.initialize({
        startOnLoad: false,
        securityLevel: "strict",
        theme: "neutral",
        fontFamily: "inherit",
      });
      return mermaid;
    });
  }
  return mermaidPromise;
};

/**
 * A diagram's opening line names its kind — `graph LR`, `flowchart TD` — which
 * is the most a short label can honestly say about it. The shape carries the
 * meaning, so the source below carries the rest.
 */
const describe = (code) => {
  const [first] = code.trim().split(/\r?\n/);
  return `${first?.trim() || "Diagram"} — full text below`;
};

const MermaidBlock = ({ code }) => {
  const [svg, setSvg] = useState("");
  const [failed, setFailed] = useState(false);
  const id = useRef(`lou-mermaid-${Math.random().toString(36).slice(2)}`);

  useEffect(() => {
    let cancelled = false;
    setFailed(false);

    loadMermaid()
      .then((mermaid) => mermaid.render(id.current, code))
      .then(({ svg: rendered }) => {
        if (!cancelled) setSvg(rendered);
      })
      .catch((error) => {
        if (cancelled) return;
        console.warn("[Lou] mermaid diagram could not be drawn", error);
        setFailed(true);
      });

    return () => {
      cancelled = true;
    };
  }, [code]);

  // Before it has drawn, and if it never does, the source is the fallback.
  if (failed || !svg) return <CodeBlock language="mermaid" code={code} />;

  return (
    <figure className="lou-mermaid">
      <div
        role="img"
        aria-label={describe(code)}
        // Mermaid returns SVG it has sanitised under `securityLevel: "strict"`.
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: svg }}
      />
      {/* The text alternative. A picture of a funnel says nothing read aloud;
          the nodes and arrows that drew it say what connects to what. */}
      <figcaption className="lou-mermaid__source">
        <details>
          <summary>Diagram source</summary>
          <pre>{code}</pre>
        </details>
      </figcaption>
    </figure>
  );
};

MermaidBlock.propTypes = { code: PropTypes.string.isRequired };

export default MermaidBlock;
